import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppSettingsService } from '../config/config-service';
import { CommonService } from '../service/common.service';

export interface RoiRun {
  _id: string;
  monthKey: string;
  runAt: string;
  portfoliosProcessed: number;
  usersCredited: number;
  totalCredited: number;
}

export interface RoiCreditDetail {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  portfolioId: string;
  portfolioName: string;
  portfolioRiskLevel: string;
  amount: number;
  creditDate: string;
  consumedAmount: number;
  monthKey: string;
}

export interface PortfolioRoiStats {
  portfolioId: string;
  portfolioName: string;
  riskLevel: string;
  totalUsers: number;
  totalCredited: number;
  totalAvailable: number;
  lastCreditDate: string;
  actualRoiPercentage: number;
}

export interface RoiCreditsResponse {
  success: boolean;
  data: {
    credits: RoiCreditDetail[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminRoiService {
  private apiUrl: string;

  constructor(
    private http: CommonService,
    private appSettingsService: AppSettingsService
  ) {
    this.apiUrl = this.appSettingsService.getSettings().apiEndPoint || '';
  }

  // Get all ROI runs history
  getRoiRuns(): Observable<ApiResponse<RoiRun[]>> {
    return this.http.get<ApiResponse<RoiRun[]>>(`${this.apiUrl}/api/admin/roi/runs`);
  }

  // Get ROI credits with filtering and pagination
  getRoiCredits(params: {
    page: number;
    limit: number;
    monthKey?: string;
    portfolioId?: string;
    search?: string;
  }): Observable<RoiCreditsResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('limit', params.limit.toString());

    if (params.monthKey) {
      httpParams = httpParams.set('monthKey', params.monthKey);
    }
    if (params.portfolioId) {
      httpParams = httpParams.set('portfolioId', params.portfolioId);
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }

    return this.http.get<RoiCreditsResponse>(`${this.apiUrl}/api/admin/roi/credits`, { params: httpParams });
  }

  // Get portfolio ROI statistics
  getPortfolioRoiStats(): Observable<ApiResponse<PortfolioRoiStats[]>> {
    return this.http.get<ApiResponse<PortfolioRoiStats[]>>(`${this.apiUrl}/api/admin/roi/portfolio-stats`);
  }

  // Trigger ROI job manually
  triggerRoiJob(runDate?: string): Observable<ApiResponse<any>> {
    const body = runDate ? { runDate } : {};
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/api/admin/trigger`, body);
  }

  // Get detailed ROI run information
  getRoiRunDetails(runId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/api/admin/roi/runs/${runId}`);
  }

  // Get ROI summary statistics
  getRoiSummary(): Observable<ApiResponse<{
    totalRuns: number;
    totalUsersCredited: number;
    totalAmountCredited: number;
    activePortfolios: number;
    lastRunDate: string;
    averageRoiRate: number;
  }>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/api/admin/roi/summary`);
  }
}
